'use client';

import { FC, JSX, useEffect, useState } from 'react';
import { css } from '@bts-web/utils-style-engine';
import { MINUTES_THRESHOLD_FOR_MARKET_STATUS_CHANGE_NOTIFICATIONS } from '@bts-web/core-features/config';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { HTMLRenderer } from '../../../../common/components/HTMLRenderer/HTMLRenderer';
import { FilledHalfmoon } from '../../../../common/svgIcons/FilledHalfmoon';
import { FilledSunrise } from '../../../../common/svgIcons/FilledSunrise';
import {
  getMinutesUntil,
  formatToWeekdayPlusHourAndMinutes,
} from '@bts-web/utils-formatting';

type MarketStatusBannerProps = {
  locale: string;
  marketClosedUntil?: Date;
  marketClosingAt?: Date;
};

const MarketStatusBanner: FC<MarketStatusBannerProps> = ({
  marketClosedUntil,
  marketClosingAt,
  locale,
}) => {
  const { t } = useClientTranslation();

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);

    return () => clearInterval(id);
  }, []);

  const minutesUntilClose = marketClosingAt
    ? getMinutesUntil(marketClosingAt, now)
    : null;

  const minutesUntilOpen = marketClosedUntil
    ? getMinutesUntil(marketClosedUntil, now)
    : null;

  const getBannerContent = (): { icon: JSX.Element; text: string } | null => {
    if (
      marketClosingAt &&
      minutesUntilClose !== null &&
      minutesUntilClose <=
        MINUTES_THRESHOLD_FOR_MARKET_STATUS_CHANGE_NOTIFICATIONS &&
      minutesUntilClose > 0
    ) {
      return {
        icon: <FilledSunrise size={16} color="warning.fill_primary" />,
        text: t('stock_market_closes_in', { minutes: minutesUntilClose }),
      };
    }

    if (
      marketClosedUntil &&
      minutesUntilOpen !== null &&
      minutesUntilOpen <=
        MINUTES_THRESHOLD_FOR_MARKET_STATUS_CHANGE_NOTIFICATIONS &&
      minutesUntilOpen > 0
    ) {
      return {
        icon: <FilledHalfmoon size={16} color="warning.fill_primary" />,
        text: t('stock_market_opens_in', { minutes: minutesUntilOpen }),
      };
    }

    if (marketClosedUntil) {
      return {
        icon: <FilledHalfmoon size={16} />,
        text: t('stock_market_closed_until', {
          date: formatToWeekdayPlusHourAndMinutes(marketClosedUntil, locale),
        }),
      };
    }

    return null;
  };

  const bannerContent = getBannerContent();

  if (!bannerContent) return null;

  const { icon, text } = bannerContent;

  return (
    <div
      aria-live="polite"
      className={css({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'extra_small',
      })}
    >
      {icon}
      <span
        className={css({
          fontSize: 'body.medium',
          '& [data-element="markdown-style"]': {
            color: 'neutrals.text_primary',
          },
        })}
      >
        <HTMLRenderer>{text}</HTMLRenderer>
      </span>
    </div>
  );
};

export { MarketStatusBanner };
